import { useEffect, useRef, useState } from 'react';
import { useRealTime } from '../contexts/RealTimeContext';

/**
 * Custom hook to handle delayed SignalR connection initialization
 * Helps prevent connection overload for supervisor/admin roles
 */
export const useDelayedSignalR = (userId: string, userRole: string, delay: number = 500) => {
  const realTime = useRealTime();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const connectionAttempted = useRef(false);
  const connectionTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const connectToSignalR = async () => {
      // Skip if already connected or attempted
      if (connectionAttempted.current || realTime.state.connectionState !== 'Disconnected') {
        return;
      }

      connectionAttempted.current = true;
      setIsConnecting(true);
      setConnectionError(null);

      try {
        // Add a small delay for supervisor/admin roles to prevent connection rush
        if (userRole === 'Supervisor' || userRole === 'Admin') {
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        console.log(`${userRole}: Attempting SignalR connection for user ${userId}`);
        
        // Set a timeout for the connection attempt
        const timeoutPromise = new Promise<never>((_, reject) => {
          connectionTimeout.current = setTimeout(() => {
            reject(new Error('SignalR connection timeout after 10 seconds'));
          }, 10000);
        });

        // Race between connection and timeout
        await Promise.race([
          realTime.connect(userId, userRole),
          timeoutPromise
        ]);

        // Clear timeout on successful connection
        if (connectionTimeout.current) {
          clearTimeout(connectionTimeout.current);
          connectionTimeout.current = null;
        }

        console.log(`${userRole}: SignalR connection established successfully`);
      } catch (error) {
        console.error(`${userRole}: SignalR connection failed:`, error);
        setConnectionError(error as Error);
        
        // For production, don't retry automatically to prevent loops
        if (process.env.NODE_ENV === 'development') {
          // Reset connection attempted flag to allow manual retry
          connectionAttempted.current = false;
        }
      } finally {
        setIsConnecting(false);
        
        // Clear any remaining timeout
        if (connectionTimeout.current) {
          clearTimeout(connectionTimeout.current);
          connectionTimeout.current = null;
        }
      }
    };

    connectToSignalR();

    // Cleanup function
    return () => {
      if (connectionTimeout.current) {
        clearTimeout(connectionTimeout.current);
        connectionTimeout.current = null;
      }
    };
  }, [userId, userRole, delay, realTime]);

  // Manual retry function
  const retryConnection = () => {
    connectionAttempted.current = false;
    setConnectionError(null);
    // Trigger re-connection by changing a dependency
    window.location.reload();
  };

  return {
    isConnecting,
    connectionError,
    connectionState: realTime.state.connectionState,
    isConnected: realTime.state.isConnected,
    retryConnection
  };
};